import { LoadingArea } from '@consigli/facade';
import {
  useGetVersionComparisonsSummaryQuery,
  usePackage,
  usePackageByTypeAndPackageId,
  usePackageId,
  useProjectId,
  useServiceName,
} from '@consigli/hooks';
import { type DocumentPackageType, type VersionComparisonSummary } from '@consigli/types';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { ServiceIdIndexValue } from '@/routes';
import { useDemoMode } from '@/util/demo-mode';

interface SummaryCardProps {
  label: string;
  value: number | string;
  valueClassName?: string;
}

const SummaryCard: FC<SummaryCardProps> = ({ label, value, valueClassName = 'text-gray-900' }) => (
  <div className="flex h-full flex-col justify-center gap-2 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span>
    <span className={`text-3xl font-bold ${valueClassName}`}>{value}</span>
  </div>
);

interface DocumentsCardProps {
  label: string;
  value: number;
}

const DocumentsCard: FC<DocumentsCardProps> = ({ label, value }) => (
  <div className="flex flex-1 flex-col justify-center gap-1 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span>
    <span className="text-2xl font-bold text-gray-900">{value}</span>
  </div>
);

interface DashboardContentProps {
  summary: VersionComparisonSummary;
  packageName: string | undefined;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const DashboardContent: FC<DashboardContentProps> = ({ summary, packageName, t }) => (
  <div className="flex flex-col gap-6 px-6 py-4 w-full">
    <div className="rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold text-gray-700">
        {t('version-compare.dashboard.run-details')}
      </h2>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-600">
        <span className="font-medium text-gray-800">{t('version-compare.dashboard.analysis')}</span>
        {packageName && (
          <span>
            {t('version-compare.dashboard.package')}{' '}
            <span className="font-medium text-gray-800">{packageName}</span>
          </span>
        )}
      </div>
    </div>

    <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
      <div className="flex flex-1 flex-col">
        <h2 className="mb-3 text-base font-semibold text-gray-700">
          {t('version-compare.dashboard.findings-summary')}
        </h2>
        <div className="grid flex-1 auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <SummaryCard
            label={t('version-compare.dashboard.total-changes')}
            value={summary.totalChanges}
          />
          <SummaryCard
            label={t('version-compare.dashboard.total-comments')}
            value={summary.totalComments}
          />
          <SummaryCard
            label={t('version-compare.dashboard.total-reconciled')}
            value={summary.totalAddressed}
            valueClassName="text-green-600"
          />
          <SummaryCard
            label={t('version-compare.dashboard.total-not-addressed')}
            value={summary.totalUnaddressed}
            valueClassName="text-red-500"
          />
          <SummaryCard
            label={t('version-compare.dashboard.reconciliation-rate')}
            value={`${summary.reconciliationRate}%`}
            valueClassName="text-green-600"
          />
          <SummaryCard
            label={t('version-compare.dashboard.total-indeterminate')}
            value={summary.totalIndeterminate}
            valueClassName="text-amber-500"
          />
        </div>
      </div>

      <div className="flex w-full flex-col lg:w-56">
        <h2 className="mb-3 text-base font-semibold text-gray-700">
          {t('version-compare.dashboard.pages-analysis')}
        </h2>
        <div className="flex flex-1 flex-col gap-3">
          <DocumentsCard
            label={t('version-compare.dashboard.pages-analyzed')}
            value={summary.pagesAnalyzed}
          />
          <DocumentsCard
            label={t('version-compare.dashboard.pages-with-changes')}
            value={summary.pagesWithChanges}
          />
          <DocumentsCard
            label={t('version-compare.dashboard.pages-with-no-changes')}
            value={Math.max(0, summary.pagesAnalyzed - summary.pagesWithChanges)}
          />
        </div>
      </div>
    </div>
  </div>
);

const VersionCompareDashboardContent: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { pkg } = usePackage(projectId, packageId);

  const { data: summary, isLoading } = useGetVersionComparisonsSummaryQuery({
    projectId,
    packageId,
  });

  if (isLoading) {
    return <LoadingArea title={t('document-list.loading')} />;
  }

  if (!summary) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-500">
        {t('version-compare.dashboard.no-data')}
      </div>
    );
  }

  return <DashboardContent summary={summary} packageName={pkg?.name} t={t} />;
};

export const VersionCompareDashboardPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const serviceName = useServiceName();
  const { adminFeaturesEnabled } = useDemoMode();

  const { pkg } = usePackageByTypeAndPackageId(
    projectId,
    ServiceIdIndexValue[serviceName] as DocumentPackageType,
    packageId,
  );

  const waitingForAnalysis = !adminFeaturesEnabled && !(pkg?.published ?? false);

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={waitingForAnalysis}>
        <Breadcrumb label={t('servicetabs.dashboard')} />
        {waitingForAnalysis ? <WaitingForAnalysis /> : <VersionCompareDashboardContent />}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
